import { useEffect, useRef, useState, useCallback } from 'react';
import { 
  Box, 
  IconButton, 
  Text, 
  HStack, 
  VStack, 
  Slider, 
  SliderTrack, 
  SliderFilledTrack, 
  SliderThumb,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button
} from '@chakra-ui/react';
import { FaPlay, FaPause } from 'react-icons/fa';
import { useAudio } from '../contexts/AudioContext';
import { formatTime } from '../utils/audioUtils';
import { SkipButton } from './audio/SkipButton';

interface AudioMetadata {
  title?: string;
  artist?: string;
  album?: string;
  artwork?: string;
}

export const MainAudioPlayer: React.FC<{ metadata?: AudioMetadata }> = ({ metadata }) => {
  const { 
    audioUrl, 
    isPlaying, 
    togglePlayPause, 
    audioElement, 
    selectedTimeRange, 
    setSelectedTimeRange,
    playbackRate,
    setPlaybackRate
  } = useAudio();
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [isReady, setIsReady] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seekTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSeekTime = useRef<number>(0);
  const retryCount = useRef(0);
  const maxRetries = 3;

  const speedOptions = [1, 1.1, 1.2, 1.5, 2];
  const currentSpeedIndex = speedOptions.indexOf(playbackRate);

  // Clear any pending seek timeouts
  const clearSeekTimeout = useCallback(() => {
    if (seekTimeoutRef.current) {
      console.log('Clearing seek timeout');
      clearTimeout(seekTimeoutRef.current);
      seekTimeoutRef.current = null;
    }
  }, []);

  // Setup audio element event listeners
  useEffect(() => {
    if (!audioElement) {
      console.log('Audio element not available from context');
      return;
    }

    console.log('Setting up listeners for global audio element');
    
    const handleLoadStart = () => {
      console.log('Audio load started');
      setIsReady(false);
      setError(null);
    };

    const handleLoadedMetadata = () => {
      console.log('Audio metadata loaded, duration:', audioElement.duration);
      if (audioElement.duration && !isNaN(audioElement.duration)) {
        setDuration(audioElement.duration);
      }
    };

    const handleCanPlay = () => {
      console.log('Audio can play event fired');
      setIsReady(true);
      setError(null);
      retryCount.current = 0;
      if (audioElement.duration && !isNaN(audioElement.duration)) {
        setDuration(audioElement.duration);
      }
    };

    const handleError = (e: ErrorEvent) => {
      console.error('Audio error details:', {
        error: e,
        audioElement: {
          error: audioElement?.error,
          errorCode: audioElement?.error?.code,
          errorMessage: audioElement?.error?.message,
          currentSrc: audioElement?.currentSrc,
          readyState: audioElement?.readyState,
          networkState: audioElement?.networkState,
          paused: audioElement?.paused
        }
      });
      
      setIsReady(false);
      setDuration(0);
      
      // Handle specific error cases
      if (audioElement?.error) {
        switch (audioElement.error.code) {
          case MediaError.MEDIA_ERR_NETWORK:
            if (retryCount.current < maxRetries) {
              console.log(`Retrying playback (attempt ${retryCount.current + 1}/${maxRetries})...`);
              retryCount.current++;
              // Retry loading the audio after a short delay with exponential backoff
              setTimeout(() => {
                if (audioElement) {
                  // Clear the source and reload
                  const currentSrc = audioElement.src;
                  audioElement.src = '';
                  audioElement.load();
                  // Set the source back and reload
                  setTimeout(() => {
                    if (audioElement) {
                      audioElement.src = currentSrc;
                      audioElement.load();
                    }
                  }, 100);
                }
              }, Math.min(1000 * Math.pow(2, retryCount.current), 8000));
              setError('Network error. Retrying...');
            } else {
              setError('Unable to load audio due to network issues. Please try again later.');
            }
            break;
          case MediaError.MEDIA_ERR_DECODE:
            setError('Unable to decode audio file. Please try again.');
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            setError('Audio format not supported.');
            break;
          case MediaError.MEDIA_ERR_ABORTED:
            if (retryCount.current < maxRetries) {
              console.log(`Retrying after abort (attempt ${retryCount.current + 1}/${maxRetries})...`);
              retryCount.current++;
              setTimeout(() => {
                if (audioElement) {
                  audioElement.load();
                }
              }, 1000);
              setError('Playback interrupted. Retrying...');
            } else {
              setError('Playback interrupted. Please try again.');
            }
            break;
          default:
            setError(`Audio error: ${audioElement.error.message || 'Unknown error'}`);
        }
      } else {
        // If we don't have specific error information, try to reload
        if (retryCount.current < maxRetries && audioElement) {
          console.log(`Retrying generic error (attempt ${retryCount.current + 1}/${maxRetries})...`);
          retryCount.current++;
          setTimeout(() => {
            if (audioElement) {
              audioElement.load();
            }
          }, 1000);
          setError('Error loading audio. Retrying...');
        } else {
          setError('An error occurred while playing the audio. Please try again.');
        }
      }
    };

    const handleTimeUpdate = () => {
      if (!isSeeking) {
        const newCurrentTime = audioElement.currentTime;
        setCurrentTime(newCurrentTime);
        
        // If we have a selected time range and we've gone beyond its end,
        // unselect it (so it doesn't stay highlighted forever)
        if (selectedTimeRange && newCurrentTime > selectedTimeRange.end + 1) {
          setSelectedTimeRange(null);
        }

        // Update position state in MediaSession API
        if ('mediaSession' in navigator && 'setPositionState' in navigator.mediaSession) {
          try {
            const currentDuration = audioElement.duration || 0;
            // Ensure position is never greater than duration
            const safePosition = Math.min(newCurrentTime, currentDuration);
            
            if (currentDuration > 0 && !isNaN(currentDuration)) {
              navigator.mediaSession.setPositionState({
                duration: currentDuration,
                playbackRate: audioElement.playbackRate || 1,
                position: safePosition
              });
            }
          } catch (error) {
            // Silently ignore errors with position state
          }
        }
      }
    };

    const handleSeeking = () => {
      console.log('Seeking started');
      setIsSeeking(true);
    };

    const handleSeeked = () => {
      console.log('Seeking completed');
      setIsSeeking(false);
    };

    // Add event listeners
    audioElement.addEventListener('loadstart', handleLoadStart);
    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioElement.addEventListener('canplay', handleCanPlay);
    audioElement.addEventListener('error', handleError);
    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    audioElement.addEventListener('seeking', handleSeeking);
    audioElement.addEventListener('seeked', handleSeeked);

    // Add canplaythrough event listener
    audioElement.addEventListener('canplaythrough', handleCanPlayThrough);

    // Initial state sync
    if (audioElement.readyState >= 2) {
      setIsReady(true);
      if (audioElement.duration && !isNaN(audioElement.duration)) {
        setDuration(audioElement.duration);
      }
      setCurrentTime(audioElement.currentTime);
    }

    // Update media session metadata
    if ('mediaSession' in navigator && metadata) {
      // Ensure the artwork URL is properly formatted and accessible
      let artworkUrl = metadata.artwork;
      
      // If the URL is relative, make it absolute
      if (artworkUrl && artworkUrl.startsWith('/')) {
        artworkUrl = window.location.origin + artworkUrl;
      }
      
      console.log('Setting MediaSession artwork URL:', artworkUrl);
      
      // Create the MediaMetadata object with proper artwork formats
      navigator.mediaSession.metadata = new MediaMetadata({
        title: metadata.title || '',
        artist: metadata.artist || '',
        album: metadata.album || '',
        artwork: artworkUrl ? [
          { src: artworkUrl, sizes: '96x96', type: 'image/png' },
          { src: artworkUrl, sizes: '128x128', type: 'image/png' },
          { src: artworkUrl, sizes: '192x192', type: 'image/png' },
          { src: artworkUrl, sizes: '256x256', type: 'image/png' },
          { src: artworkUrl, sizes: '384x384', type: 'image/png' },
          { src: artworkUrl, sizes: '512x512', type: 'image/png' }
        ] : undefined
      });

      // Add media session action handlers for skip forward/backward
      navigator.mediaSession.setActionHandler('seekbackward', (details) => {
        const skipTime = details.seekOffset || 10;
        handleSkip(-skipTime);
      });

      navigator.mediaSession.setActionHandler('seekforward', (details) => {
        const skipTime = details.seekOffset || 10;
        handleSkip(skipTime);
      });

      // Add play/pause handler
      navigator.mediaSession.setActionHandler('play', () => {
        if (audioElement && audioElement.paused) {
          audioElement.play().catch(err => console.error('Error playing audio:', err));
          if (!isPlaying) {
            togglePlayPause();
          }
        }
      });

      navigator.mediaSession.setActionHandler('pause', () => {
        if (audioElement && !audioElement.paused) {
          audioElement.pause();
          if (isPlaying) {
            togglePlayPause();
          }
        }
      });

      // Add position state to show progress in lock screen
      if ('setPositionState' in navigator.mediaSession) {
        try {
          navigator.mediaSession.setPositionState({
            duration: audioElement?.duration || 0,
            playbackRate: audioElement?.playbackRate || 1,
            position: audioElement?.currentTime || 0
          });
        } catch (error) {
          console.error('Error setting position state:', error);
        }
      }
    }

    return () => {
      console.log('Cleaning up event listeners from global audio element');
      audioElement.removeEventListener('loadstart', handleLoadStart);
      audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.removeEventListener('canplay', handleCanPlay);
      audioElement.removeEventListener('canplaythrough', handleCanPlayThrough);
      audioElement.removeEventListener('error', handleError);
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
      audioElement.removeEventListener('seeking', handleSeeking);
      audioElement.removeEventListener('seeked', handleSeeked);
    };
  }, [audioElement, isSeeking, metadata, selectedTimeRange, setSelectedTimeRange]);

  const performSeek = useCallback(async (newTime: number) => {
    if (!audioElement || !isReady) return;

    // Debounce seeks that are too close together
    const now = Date.now();
    if (now - lastSeekTime.current < 100) {
      console.log('Debouncing seek operation');
      return;
    }
    lastSeekTime.current = now;

    try {
      console.log('Starting seek operation to time:', newTime);
      setIsSeeking(true);
      clearSeekTimeout();

      const wasPlaying = !audioElement.paused;
      if (wasPlaying) {
        console.log('Pausing before seek');
        audioElement.pause();
      }

      // Set the new time
      const constrainedTime = Math.max(0, Math.min(newTime, duration));
      console.log('Setting time:', constrainedTime);
      audioElement.currentTime = constrainedTime;

      // Wait for the seek to complete
      await new Promise<void>((resolve) => {
        const handleSeeked = () => {
          console.log('Seek completed');
          audioElement.removeEventListener('seeked', handleSeeked);
          resolve();
        };
        audioElement.addEventListener('seeked', handleSeeked);
      });

      if (wasPlaying) {
        console.log('Resuming playback after seek');
        await audioElement.play();
      }
    } catch (error) {
      console.error('Seek error:', error);
    } finally {
      // Set a timeout to clear the seeking state in case the seeked event doesn't fire
      seekTimeoutRef.current = setTimeout(() => {
        console.log('Clearing seeking state via timeout');
        setIsSeeking(false);
      }, 100);
    }
  }, [audioElement, isReady, duration, clearSeekTimeout]);

  const handleSeek = useCallback((value: number) => {
    const newTime = (value / 100) * duration;
    performSeek(newTime);
  }, [duration, performSeek]);

  const handleSkip = useCallback((seconds: number) => {
    if (!audioElement) return;
    
    const newTime = audioElement.currentTime + seconds;
    performSeek(newTime);
  }, [audioElement, performSeek]);

  const handleSpeedChange = (speed: number) => {
    setPlaybackRate(speed);
  };

  // Add a canplaythrough event handler to ensure we have enough data
  const handleCanPlayThrough = () => {
    console.log('Audio can play through without buffering');
    setIsReady(true);
    setError(null);
    retryCount.current = 0;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSeekTimeout();
    };
  }, [clearSeekTimeout]);

  if (!audioUrl) return null;

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      py={2}
      px={{ base: 3, md: 6 }}
      background="linear-gradient(to right, #EAF8FF, #F4FBFF, #FFFFFF)"
      color="#404040"
      data-testid="main-audio-player"
      zIndex={1000}
    >
      <Box maxW="container.xl" mx="auto">
        <VStack spacing={2}>
          {error && (
            <Text color="red.500" fontSize="sm" textAlign="center">
              {error}
            </Text>
          )}
          <HStack spacing={{ base: 6, md: 10 }} width="100%" justify="center" align="center">
            <Box>
              <SkipButton
                seconds={-10}
                isBackward
                onClick={() => handleSkip(-10)}
                isDisabled={!isReady}
              />
            </Box>
            {/* <Box display={{ base: 'none', sm: 'block' }}>
              <SkipButton
                seconds={-15}
                isBackward
                onClick={() => handleSkip(-15)}
                isDisabled={!isReady}
              />
            </Box> */}
            <IconButton
              aria-label={isPlaying ? 'Pause' : 'Play'}
              icon={isPlaying ? <FaPause size={22} /> : <FaPlay size={22} />}
              onClick={togglePlayPause}
              isDisabled={!isReady}
              colorScheme="gray"
              size="md"
              isRound
              _hover={{
                transform: 'scale(1.05)',
                bg: 'gray.200'
              }}
              _active={{
                transform: 'scale(0.95)'
              }}
              transition="all 0.2s"
            />
            <Box>
              <SkipButton
                seconds={10}
                onClick={() => handleSkip(10)}
                isDisabled={!isReady}
              />
            </Box>
            {/* <Box display={{ base: 'none', sm: 'block' }}>
              <SkipButton
                seconds={30}
                onClick={() => handleSkip(30)}
                isDisabled={!isReady}
              />
            </Box> */}
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                variant="outline"
                colorScheme="gray"
                isDisabled={!isReady}
                minW="70px"
                ml={2}
              >
                {playbackRate}x
              </MenuButton>
              <MenuList>
                {speedOptions.map((speed) => (
                  <MenuItem
                    key={speed}
                    onClick={() => handleSpeedChange(speed)}
                    bg={playbackRate === speed ? "blue.50" : "transparent"}
                    _hover={{ bg: "blue.50" }}
                  >
                    {speed}x
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </HStack>
          <HStack width="100%" spacing={4}>
            <Text fontSize="xs" minW="50px" fontWeight="medium" color="gray.700">
              {formatTime(currentTime)}
            </Text>
            <Slider
              aria-label="Audio progress"
              value={(currentTime / duration) * 100 || 0}
              onChange={handleSeek}
              isDisabled={!isReady}
              focusThumbOnChange={false}
              colorScheme="gray"
            >
              <SliderTrack bg="gray.200" h="3px">
                <SliderFilledTrack bg="gray.600" />
              </SliderTrack>
              <SliderThumb 
                boxSize={2.5}
                bg="white"
                _focus={{
                  boxShadow: '0 0 0 3px rgba(160, 174, 192, 0.3)'
                }}
              />
            </Slider>
            <Text fontSize="xs" minW="50px" fontWeight="medium" color="gray.700">
              {formatTime(duration)}
            </Text>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
}; 